import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SetPasswordDialog } from '@/features/platform-admin/SetPasswordDialog';
import {
  usePlatformClinic,
  usePlatformClinicMembers,
  useSetClinicDisabled,
} from '@/hooks/usePlatformAdmin';
import { translateApiError } from '@/lib/apiError';
import { localeFor } from '@/lib/locale';
import type { PlatformClinicMember } from '@/types/api';

/**
 * One clinic, and everyone in it.
 *
 * The page an operator lands on when a clinic is the subject: who is affected
 * before switching it off, and who to hand a new password to when someone is
 * locked out. Reached by its id rather than its slug — a slug can be renamed,
 * and this URL should keep working when it is.
 *
 * Staff accounts only. There is deliberately nothing clinical here: a platform
 * operator can see who works at a clinic and cannot read one patient record,
 * which is the boundary `requireSuperAdmin` draws by never appearing under
 * `/api/c/:clinicSlug`.
 */
export function PlatformClinicDetailPage() {
  const { t, i18n } = useTranslation();
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(localeFor(i18n.language), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  const { clinicId } = useParams<{ clinicId: string }>();
  const [target, setTarget] = useState<PlatformClinicMember | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const clinicQuery = usePlatformClinic(clinicId);
  const membersQuery = usePlatformClinicMembers(clinicId);
  const toggle = useSetClinicDisabled();

  const clinic = clinicQuery.data;

  if (clinicQuery.isLoading) {
    return <p className="px-1 py-10 text-center text-[13px] text-muted">{t('platformAdmin.loading')}</p>;
  }
  if (clinicQuery.error || !clinic) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <p className="px-1 py-10 text-center text-[13px] text-destructive">
          {translateApiError(clinicQuery.error, {
            fallback: t('platformAdmin.clinicDetail.loadFailed'),
          })}
        </p>
      </div>
    );
  }

  const members = membersQuery.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-[-0.02em]">{clinic.name}</h1>
            {clinic.disabled && (
              <Badge tone="danger" size="sm">
                {t('platformAdmin.clinics.disabledBadge')}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-[13.5px] text-muted">
            /{clinic.slug} · {t('platformAdmin.clinics.memberCount', { count: clinic.memberCount })}{' '}
            · {t('platformAdmin.clinicDetail.created', { date: formatDate(clinic.createdAt) })}
          </p>
        </div>
        <Button
          size="sm"
          variant={clinic.disabled ? 'secondary' : 'danger'}
          disabled={toggle.isPending}
          onClick={() => toggle.mutate({ id: clinic.id, disabled: !clinic.disabled })}
        >
          {clinic.disabled ? t('platformAdmin.clinics.enable') : t('platformAdmin.clinics.disable')}
        </Button>
      </header>

      {toggle.isError && (
        <div
          role="alert"
          className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive"
        >
          {translateApiError(toggle.error, { fallback: t('platformAdmin.clinics.toggleFailed') })}
        </div>
      )}

      {done && (
        <div
          role="status"
          className="rounded-md border border-success/30 bg-success-bg/40 px-3 py-2 text-[12.5px] text-success"
        >
          {t('platformAdmin.passwords.done', { email: done })}
        </div>
      )}

      <Card className="overflow-hidden p-0">
        <div className="border-b border-line px-4 py-3 sm:px-5">
          <h2 className="text-[14px] font-semibold tracking-[-0.01em]">
            {t('platformAdmin.clinicDetail.membersTitle')}
          </h2>
        </div>

        {membersQuery.isLoading ? (
          <div className="px-5 py-10 text-center text-[13px] text-muted">
            {t('platformAdmin.loading')}
          </div>
        ) : membersQuery.error ? (
          <div className="px-5 py-10 text-center text-[13px] text-destructive">
            {translateApiError(membersQuery.error, {
              fallback: t('platformAdmin.clinicDetail.membersLoadFailed'),
            })}
          </div>
        ) : members.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-muted">
            {t('platformAdmin.clinicDetail.noMembers')}
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {members.map((member) => (
              <li key={member.id} className="flex items-center gap-3 px-4 py-3.5 sm:px-5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[14px] font-semibold tracking-[-0.01em]">
                      {member.name}
                    </span>
                    {member.roles.map((role) => (
                      <Badge key={role.id} tone="neutral" size="sm">
                        {role.name}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-0.5 truncate text-[12.5px] text-muted">{member.email}</div>
                </div>
                <span className="hidden text-[11.5px] text-muted sm:inline">
                  {t('platformAdmin.clinicDetail.joined', { date: formatDate(member.joinedAt) })}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={`${t('platformAdmin.passwords.setPassword')} ${member.email}`}
                  onClick={() => {
                    setDone(null);
                    setTarget(member);
                  }}
                >
                  {t('platformAdmin.passwords.setPassword')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {target && (
        <SetPasswordDialog
          user={target}
          onClose={() => setTarget(null)}
          onDone={(email) => {
            setTarget(null);
            setDone(email);
          }}
        />
      )}
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation();
  return (
    <Link to="/admin/clinics" className="text-[12.5px] text-muted hover:text-ink">
      ← {t('platformAdmin.clinicDetail.back')}
    </Link>
  );
}
